import { Close, ImageOutlined, UploadOutlined } from '@mui/icons-material'
import { Box, FormControl, FormHelperText, IconButton, Stack, Typography, useMediaQuery, Button } from '@mui/material'
import React, { FunctionComponent, ReactElement, SetStateAction } from 'react'
import gaiaTheme from '../../../../root-components/theme'
import { TooltipButton } from '../../miscellaneous-components/tooltip-button'
import { FileUploadHelper } from '../../miscellaneous-components/file-upload-helper'
import FormEditButtons from './form-edit-buttons'

export const FormImageInputDisplay: FunctionComponent<{
  isEditPage: boolean,
  allowEdit?: boolean,
  editField?: string | undefined,
  setEditField?: React.Dispatch<SetStateAction<any>> | undefined,
  hideEditIcon?: boolean,
  disabled: boolean,
  isLastField?: boolean,
  title: string,
  name: string,
  value: string | undefined,
  touched?: any,
  errors?: any,
  onChange: any,
  onImageUpload?: () => void,
  onImageRemove?: () => void,
  onImageLibrarySelect?: () => void,
  cancelEditHandler?: () => void,
  customErrorMessage?: string,
  tooltip?: string,
  imageWidth?: number,
  imageHeight?: number,
  uploadButtonText?: string,
  showImageLibraryButton?: boolean,
  imageLibraryButtonText?: string,
  placeholderIcon?: ReactElement,
  // FileUploadHelper props
  fileData?: any,
  setFileData?: React.Dispatch<SetStateAction<any>>,
  onFileUploadSubmit?: () => void,
  disableUploadButton?: boolean,
  hideSaveButton?: boolean,
  hidePreview?: boolean,
  bottomBorder?: boolean,
  topBorder?: boolean,
  sideBorders?: boolean,
  isTopRow?: boolean,
  isBottomRow?: boolean,
  /** When true, field label uses regular weight (e.g. section headings stay bold elsewhere). */
  regularFieldLabel?: boolean,
}> = ({
  isEditPage,
  allowEdit = true,
  editField,
  setEditField,
  hideEditIcon = false,
  disabled,
  isLastField = false,
  title,
  name,
  value,
  touched,
  errors,
  onChange,
  onImageUpload,
  onImageRemove,
  onImageLibrarySelect,
  cancelEditHandler,
  customErrorMessage,
  tooltip,
  imageWidth = 75,
  imageHeight = 75,
  uploadButtonText = 'Upload Image',
  showImageLibraryButton = false,
  imageLibraryButtonText = 'Choose from Image Library',
  placeholderIcon,
  fileData,
  setFileData,
  onFileUploadSubmit,
  disableUploadButton = false,
  hideSaveButton = false,
  hidePreview = false,
  bottomBorder = false,
  topBorder = true,
  sideBorders = true,
  isTopRow = false,
  isBottomRow = false,
  regularFieldLabel = false,
}) => {

    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

    const toggleEditField = (fieldName: string) => {
      if (setEditField) {
        setEditField(fieldName)
      }
    }

    const handleImageChange = (newValue: string) => {
      if (onChange) {
        onChange({ target: { name, value: newValue } });
      }
    }

    return (
      <FormControl fullWidth variant='outlined'>
        <Stack sx={{
          borderWidth: '1px',
          borderBottomStyle: bottomBorder || isBottomRow ? 'solid' : 'none',
          borderColor: 'lightGrey.main',
          borderLeftStyle: sideBorders ? 'solid' : 'none',
          borderRightStyle: sideBorders ? 'solid' : 'none',
          borderTopStyle: topBorder || isTopRow ? 'solid' : 'none',
          pt: 1, pb: 1, pl: 3,
          borderTopLeftRadius: isTopRow ? '10px' : 0,
          borderTopRightRadius: isTopRow ? '10px' : 0,
          borderBottomLeftRadius: isBottomRow ? '10px' : 0,
          borderBottomRightRadius: isBottomRow ? '10px' : 0
        }}>
          <Stack position='relative' alignItems={isMobileMode ? 'flex-start' : 'center'} flexDirection={isMobileMode ? 'column' : 'row'} justifyContent='start'>
            <Stack sx={{ width: isMobileMode ? '100%' : '40%', minWidth: '100px', color: 'text.primary', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <Typography variant='subtitle1' sx={regularFieldLabel ? { fontWeight: 400 } : undefined}>{title}</Typography>
              {tooltip && !isMobileMode && <TooltipButton
                text={tooltip}
                noPadding={true}
                placement='bottom' />}
            </Stack>
            <Stack position='relative' flexDirection='column' sx={{ width: '100%' }}>
              <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'>
                <Stack direction={isMobileMode ? 'column' : 'row'} alignItems={isMobileMode ? 'flex-start' : 'center'} sx={{ width: '100%' }}>
                  {!fileData || fileData.length === 0 ? (
                    <Box sx={{ mr: 3, mb: isMobileMode ? 2 : 0, ml: 2 }}>
                      {value ? (
                        <Box sx={{ position: 'relative', '&:hover .remove-button': { display: 'flex', ml: 2 } }}>
                          <img
                            src={value}
                            alt={title}
                            style={{
                              width: `${imageWidth}px`,
                              height: `${imageHeight}px`,
                              objectFit: 'cover',
                              borderRadius: '8px'
                            }}
                          />
                          {onImageRemove && !disabled && (
                            <IconButton
                              onClick={() => {
                                onImageRemove();
                                handleImageChange('');
                              }}
                              className="remove-button"
                              sx={{
                                position: 'absolute',
                                top: -8,
                                right: -8,
                                backgroundColor: 'text.secondary',
                                color: 'common.white',
                                width: '20px',
                                height: '20px',
                                display: 'none',
                                '&:hover': {
                                  backgroundColor: 'text.primary',
                                }
                              }}
                            >
                              <Close sx={{ fontSize: '12px' }} />
                            </IconButton>
                          )}
                        </Box>
                      ) : (
                        <Box sx={{
                          width: `${imageWidth}px`,
                          height: `${imageHeight}px`,
                          backgroundColor: 'primary.light',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          {placeholderIcon || <ImageOutlined sx={{ color: 'common.white', fontSize: '2rem' }} />}
                        </Box>
                      )}
                    </Box>
                  ) : null}
                  <Stack direction='row' spacing={2} alignItems='center' sx={{ml:2}}>
                    {fileData && setFileData && onFileUploadSubmit ? (
                        <FileUploadHelper
                          disableButton={disabled}
                          fileData={fileData}
                          setFileData={setFileData}
                          buttonCopy={uploadButtonText}
                          onSubmit={onFileUploadSubmit}
                          hidePreview={hidePreview}
                        />
                    ) : onImageUpload ? (
                      <Button
                        variant="outlined"
                        onClick={onImageUpload}
                        disabled={disabled}
                        sx={{ maxHeight: '44px' }}
                      >
                        <UploadOutlined />
                        {uploadButtonText}
                      </Button>
                    ) : null}
                    {showImageLibraryButton && onImageLibrarySelect && (
                      <Button
                        variant="text"
                        onClick={onImageLibrarySelect}
                        disabled={disabled}
                        sx={{ maxHeight: '44px' }}
                      >
                        <ImageOutlined />
                        <Typography sx={{ p: 0.5 }}>{imageLibraryButtonText}</Typography>
                      </Button>
                    )}
                  </Stack>
                </Stack>
                {allowEdit &&
                  <FormEditButtons
                    isEditPage={isEditPage}
                    editField={editField}
                    hideEditIcon={hideEditIcon}
                    name={name}
                    toggleEditField={toggleEditField}
                    cancelEditHandler={cancelEditHandler}
                  />}
              </Stack>
              {touched && errors &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {errors ? <div>{errors as string}</div> : null}
                </FormHelperText>
              }
              {customErrorMessage &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{customErrorMessage}
                </FormHelperText>
              }
            </Stack>
          </Stack>
        </Stack>
      </FormControl>
    )
  }